import { interceptorMiddleware } from '../../../shared/services/interceptor.service';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ListResponse } from '../../../shared/utils/types.elements';
import {
    CreateSpecial,
    CreateSpecialApiParams,
    LocalSpecials,
} from '../utils/types.specials';
import { apiUrlSpecials } from '../constants/apiUrl.specials';
import { ConstNumber } from '../../../shared/constants/number.constant';
import {
    formatChangeDateTime,
    formatDateAndTime,
    mapFormDataFields,
} from '../../../shared/utils/helper';
import {
    ApiParamEnum,
    HttpMethods,
} from '../../../shared/constants/application.constant';
import { getChangedValues } from '../utils/util';

// Define a service using a base URL and expected endpoints
export const specialsApi = createApi({
    reducerPath: 'specials',
    baseQuery: interceptorMiddleware,
    tagTypes: ['specials', 'special'],
    endpoints: (builder) => ({
        getSpecials: builder.query<
            ListResponse<LocalSpecials>,
            { page: number; offerType: number }
        >({
            query: ({ page, offerType }) => ({
                url: `${
                    apiUrlSpecials.SPECIALS
                }?offer_type=${offerType}&limit=${ApiParamEnum.LIMIT}&offset=${
                    (page - ConstNumber.VALUE_1) * ApiParamEnum.OFFSET
                }`,
                method: 'GET',
            }),
            providesTags: ['specials'],
        }),
        createSpecial: builder.mutation({
            query: (data: CreateSpecialApiParams) => {
                const specialDataPayload = {
                    start_datetime: formatDateAndTime(
                        data.start_date,
                        data.start_time
                    ),
                    end_datetime: formatDateAndTime(
                        data.end_date,
                        data.end_time
                    ),
                    name: data.name,
                    address: data.address,
                    description: data?.description || '',
                    offer_type: data?.offer_type?.toString() || '',
                    opening_hours_list: data?.opening_hours_list || '',
                    price_range: data?.price_range || null,
                    document:
                        typeof data?.document === 'string'
                            ? data?.document
                            : data?.s3MediaUrl,
                    display_start_datetime: formatChangeDateTime(
                        data.start_date,
                        data.start_time
                    ),
                    display_end_datetime: formatChangeDateTime(
                        data.end_date,
                        data.end_time
                    ),
                };
                return {
                    url: apiUrlSpecials.SPECIALS,
                    method: HttpMethods.POST,
                    body: specialDataPayload,
                };
            },
        }),
        updateSpecial: builder.mutation<
            {
                message: string;
            },
            { data: CreateSpecial; initialValues: CreateSpecial }
        >({
            query: ({ data, initialValues }) => {
                const changedValues = getChangedValues(initialValues, data);
                if ('document' in changedValues) {
                    changedValues['document'] =
                        changedValues['s3MediaUrl'] || '';
                }
                delete changedValues['s3MediaUrl'];
                return {
                    url: `${apiUrlSpecials.SPECIALS}${data.id}/`,
                    method: HttpMethods.PATCH,
                    body: changedValues,
                };
            },
        }),
        getSpecialById: builder.query<LocalSpecials, number>({
            query: (id: number) => ({
                url: `${apiUrlSpecials.SPECIALS}${id}/`,
                method: HttpMethods.GET,
            }),
            providesTags: [],
            keepUnusedDataFor: ConstNumber.VALUE_1,
        }),
        deleteSpecials: builder.mutation<{ message: string }, { id: number }>({
            query: ({ id }) => {
                return {
                    url: `${apiUrlSpecials.SPECIALS}${id}/`,
                    method: HttpMethods.DELETE,
                };
            },
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetSpecialsQuery,
    useCreateSpecialMutation,
    useGetSpecialByIdQuery,
    useUpdateSpecialMutation,
    useDeleteSpecialsMutation,
} = specialsApi;
